import { Flex, Group, Tabs, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { ApprovalReasonsTab } from "@/components/features/AccountsPayable/Management/ApprovalReasonsTab";
import { ExpenseTypesTab } from "@/components/features/AccountsPayable/Management/ExpenseTypesTab";
import { PaymentAccountsTab } from "@/components/features/AccountsPayable/Management/PaymentAccountsTab";
import { PaymentFieldsTab } from "@/components/features/AccountsPayable/Management/PaymentFieldsTab";
import { PaymentMethodsTab } from "@/components/features/AccountsPayable/Management/PaymentMethodsTab";
import { PaymentTermsTab } from "@/components/features/AccountsPayable/Management/PaymentTermsTab";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

import type { TabItemType } from "@/types/common.types";

import styles from "./accounts-payable.module.scss";

const manageSearchSchema = z.object({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/accounts-payable/manage")({
  component: AccountsPayableManage,
  validateSearch: manageSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const MANAGE_TABS: TabItemType[] = [
  { value: "expense-types", label: "Expense Types", component: <ExpenseTypesTab /> },
  { value: "approval-reasons", label: "Approval Reasons", component: <ApprovalReasonsTab /> },
  { value: "payment-terms", label: "Payment Terms", component: <PaymentTermsTab /> },
  { value: "payment-accounts", label: "Payment Accounts", component: <PaymentAccountsTab /> },
  { value: "payment-methods", label: "Payment Methods", component: <PaymentMethodsTab /> },
  { value: "payment-fields", label: "Payment Fields", component: <PaymentFieldsTab /> },
];

function AccountsPayableManage() {
  useBreadcrumb(
    [
      {
        title: "Accounts Payable",
        href: "/accounts-payable",
        icon: getRouteIcon(MODULE_KEYS_MAP.accountsPayable),
      },
      { title: "Manage", href: "/accounts-payable/manage" },
    ],
    { listView: "List View" },
  );

  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "expense-types" });

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          Manage Accounts Payable
        </Title>
      </Group>

      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value || "expense-types")}
        keepMounted={false}
        className={styles.tab}
      >
        <Tabs.List className={styles.tabsList}>
          {MANAGE_TABS.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {MANAGE_TABS.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
            {tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Flex>
  );
}
